import { useEffect, useRef } from 'react';

interface IdentifySyncMessage {
    type: 'pageNumber';
    value: number;
    timestamp: number;
}

interface UseIdentifySyncProps {
    currentPage: number;
    onPageChange: (pageNumber: number) => void;
}

/**
 * Custom hook for synchronizing current page across browser tabs for identify pages
 * Uses BroadcastChannel API with localStorage fallback for older browsers
 */
export const useIdentifySync = ({
    currentPage,
    onPageChange,
}: UseIdentifySyncProps) => {
    const channelRef = useRef<BroadcastChannel | null>(null);
    const lastReceivedRef = useRef<number | undefined>(undefined);
    const lastExternalPageRef = useRef<number | null>(null);

    // Initialize BroadcastChannel or localStorage listener
    useEffect(() => {
        const channelName = 'identify-pages-sync';

        // Try to use BroadcastChannel if available
        if (typeof BroadcastChannel !== 'undefined') {
            channelRef.current = new BroadcastChannel(channelName);

            channelRef.current.onmessage = (event: MessageEvent<IdentifySyncMessage>) => {
                const { value, timestamp } = event.data;

                // Ignore messages we've already processed
                if (lastReceivedRef.current === timestamp) {
                    return;
                }

                lastReceivedRef.current = timestamp;
                lastExternalPageRef.current = value;
                onPageChange(value);
            };
        } else {
            // Fallback to localStorage
            const handleStorageChange = (e: StorageEvent) => {
                if (e.key === 'identify-sync' && e.newValue) {
                    try {
                        const data: IdentifySyncMessage = JSON.parse(e.newValue);
                        const { value, timestamp } = data;

                        // Ignore messages we've already processed
                        if (lastReceivedRef.current === timestamp) {
                            return;
                        }

                        lastReceivedRef.current = timestamp;
                        lastExternalPageRef.current = value;
                        onPageChange(value);
                    } catch (error) {
                        console.error('Failed to parse identify sync message:', error);
                    }
                }
            };

            window.addEventListener('storage', handleStorageChange);

            return () => {
                window.removeEventListener('storage', handleStorageChange);
            };
        }

        return () => {
            if (channelRef.current) {
                channelRef.current.close();
                channelRef.current = null;
            }
        };
    }, [onPageChange]);

    // Broadcast page changes
    useEffect(() => {
        // Skip broadcasting if this value came from an external sync message
        if (lastExternalPageRef.current === currentPage) {
            lastExternalPageRef.current = null;
            return;
        }

        if (currentPage === 0) {
            return;
        }

        const message: IdentifySyncMessage = {
            type: 'pageNumber',
            value: currentPage,
            timestamp: Date.now(),
        };

        if (channelRef.current) {
            channelRef.current.postMessage(message);
        } else {
            // Fallback to localStorage
            localStorage.setItem('identify-sync', JSON.stringify(message));
        }
    }, [currentPage]);
};
